import { Box } from '@televet/kibble-ui/build/chakra';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Menu } from '@televet/kibble-ui/build/components/Menu';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import format from 'date-fns/format';
import React, { useCallback, useState } from 'react';
import { FeatureFlagName, GraphQLFetchPolicies } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { IntegrationSystemDisplayNameType } from 'shared/providers/IntegrationsProvider/types/IntegrationSystem';
import {
  PaymentReportFinancialTransactionFragment,
  ReportFilterTab,
  StripeDisputeSelectionFragment,
  StripePayoutSelectionFragment,
  useGetDisbursementsLazyQuery,
  useGetDisputesLazyQuery,
  useGetFinancialTransactionsLazyQuery,
} from 'shared/types/graphql';
import { IColumnSelection, PaymentReportQueriesVariables } from '../types';
import { buildDisbursementsData } from '../utils/buildDisbursementsData';
import { buildDisputesData } from '../utils/buildDisputesData';
import { buildTransactionsData } from '../utils/buildTransactionsData';
import { convertArrayOfObjectsToCSV, CSVRow } from '../utils/csv';

interface DownloadProps {
  columns: IColumnSelection[];
  clinicId: string;
  isPaymentConfigInvalid: boolean;
  primaryIntegrationName: IntegrationSystemDisplayNameType | null;
  isClientIdSupported: boolean;
  queryVariables: PaymentReportQueriesVariables;
}

interface PrintProps {
  onPrint: () => void;
  isPrinting: boolean;
}

interface ExportMenuProps {
  isLoadingTableData: boolean;
  onSelectCallback?: () => void;
  printProps: PrintProps;
  downloadProps: DownloadProps;
}

export const ExportMenu = ({
  isLoadingTableData,
  onSelectCallback,
  printProps,
  downloadProps,
}: ExportMenuProps): JSX.Element => {
  const { onPrint, isPrinting } = printProps;
  const { columns, clinicId, isPaymentConfigInvalid, isClientIdSupported, primaryIntegrationName, queryVariables } =
    downloadProps;
  const { hasAdminRoleAtCurrentClinic } = useClinicUser();
  const { isFeatureEnabled } = useFeatureFlag();
  const [isDownloading, setIsDownloading] = useState(false);
  const toast = useToast();

  const downloadCSV = useCallback(
    (array: CSVRow[]) => {
      if (!array.length) {
        setIsDownloading(false);
        toast({
          status: 'error',
          title: 'There is no data to download, please adjust your filters',
        });
        return;
      }
      const link = document.createElement('a');

      const csv = convertArrayOfObjectsToCSV({
        array,
        columns,
      });

      if (csv === null) return;

      const filename = `Otto_Financial_Report_${format(new Date(), "MM-dd-yy'_'_h_mm_aaaaa'm'")}.csv`;
      const blobx = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blobx);
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);

      link.click();
      link.parentNode?.removeChild(link);

      setIsDownloading(false);
    },
    [columns, toast],
  );

  const [getFullTransactions] = useGetFinancialTransactionsLazyQuery({
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    nextFetchPolicy: GraphQLFetchPolicies.CacheFirst,
    onCompleted: (data) => {
      const _data = buildTransactionsData({
        rawData: data.getFinancialTransactions.data as PaymentReportFinancialTransactionFragment[],
        displayType: 'Download',
        isPaymentConfigInvalid,
        primaryIntegrationName,
        isClientIdSupported,
        hasAdminRoleAtCurrentClinic,
        hasInvoiceDiscovery: isFeatureEnabled(FeatureFlagName.InvoiceDiscovery),
        hasBitwerxPaymentWritebacks: isFeatureEnabled(FeatureFlagName.BitwerxPaymentWritebacks),
        columns,
      });

      downloadCSV(_data);
    },
    onError: (e) => {
      console.error(e);
      toast({
        status: 'error',
        title: 'There was an error downloading the report, please try again',
      });
    },
  });

  const [getFullPayouts] = useGetDisbursementsLazyQuery({
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    onCompleted: (data) => {
      const _data = buildDisbursementsData({
        rawData: (data.getDisbursements.data as StripePayoutSelectionFragment[]) || [],
        displayType: 'Download',
        columns,
        hasEOD: isFeatureEnabled(FeatureFlagName.EODPayoutControls),
      });

      downloadCSV(_data);
    },
    onError: (e) => {
      console.error(e);
      toast({
        status: 'error',
        title: 'There was an error downloading the report, please try again',
      });
    },
  });

  const [getFullDisputes] = useGetDisputesLazyQuery({
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    onCompleted: (data) => {
      const _data = buildDisputesData({
        rawData: (data.getDisputes.data as StripeDisputeSelectionFragment[]) || [],
        displayType: 'Download',
        columns,
      });

      downloadCSV(_data);
    },
    onError: (e) => {
      console.error(e);
      toast({
        status: 'error',
        title: 'There was an error downloading the report, please try again',
      });
    },
  });

  const handleDownload = async (): Promise<void> => {
    if (isDownloading) {
      return;
    }

    if (onSelectCallback) {
      onSelectCallback();
    }

    if (!columns.length) {
      toast({
        status: 'error',
        title: 'There is no data to download, please adjust your filters',
      });
    } else {
      setIsDownloading(true);
      if (queryVariables.selectedFilterTab === ReportFilterTab.Disbursements) {
        await getFullPayouts({
          variables: {
            data: {
              clinicId,
              timestamp: {
                startDate: queryVariables.selectionRange.startDate.toISOString(),
                endDate: queryVariables.selectionRange.endDate.toISOString(),
              },
            },
            sortBy: queryVariables.sortBy,
          },
        });
      } else if (queryVariables.selectedFilterTab === ReportFilterTab.Disputes) {
        const disputeStatus =
          queryVariables.disputeStatusFiltering !== 'All' ? queryVariables.disputeStatusFiltering : undefined;
        await getFullDisputes({
          variables: {
            data: {
              clinicId,
              ...(!queryVariables.selectionRange.shouldIgnore && {
                timestamp: {
                  startDate: queryVariables.selectionRange.startDate.toISOString(),
                  endDate: queryVariables.selectionRange.endDate.toISOString(),
                },
              }),
              includeSubscriptions: queryVariables.showSubscriptions,
              disputeStatusFiltering: disputeStatus,
            },
            sortBy: queryVariables.sortBy,
          },
        });
      } else {
        const cardBrand = queryVariables.cardBrandFiltering !== 'All' ? queryVariables.cardBrandFiltering : undefined;
        const cardType = queryVariables.cardTypeFiltering !== 'All' ? queryVariables.cardTypeFiltering : undefined;
        const payoutId = queryVariables.selectedPayout?.id;
        await getFullTransactions({
          variables: {
            data: {
              clinicId,
              ...(!payoutId && {
                timestamp: {
                  startDate: queryVariables.selectionRange.startDate.toISOString(),
                  endDate: queryVariables.selectionRange.endDate.toISOString(),
                },
              }),
              ...(payoutId && {
                stripePayoutId: payoutId,
              }),
              reportFilterTab: queryVariables.selectedFilterTab,
              includeSubscriptions: queryVariables.showSubscriptions,
              cardBrand,
              cardType,
              clientName: queryVariables.nameFiltering,
              terminalNames: queryVariables.excludedTerminalNames,
            },
            sortBy: queryVariables.sortBy,
          },
        });
      }
    }
  };

  return (
    <Box className="PaymentReport__ExportMenu">
      <Menu
        size="sm"
        buttonProps={{ size: 'sm', variant: 'secondary', text: 'Export', leftIconName: 'upload' }}
        listProps={{
          menuItems: [
            {
              label: 'Print',
              value: 'print',
              leftElement: <Icon name="printer" size="sm" />,
              onSelect: (): void => {
                if (onSelectCallback) {
                  onSelectCallback();
                }
                onPrint();
              },
              isDisabled: isPrinting || isLoadingTableData,
            },
            {
              label: 'Download CSV',
              value: 'download',
              leftElement: <Icon name="download" size="sm" />,
              onSelect: handleDownload,
              isDisabled: isDownloading || isLoadingTableData,
            },
          ],
        }}
      />
    </Box>
  );
};
